import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { toast as anvil2Toast } from '@servicetitan/anvil2';
import { proxy } from 'valtio';
import {
  ToastState,
  ToastOptions,
  Toast,
  IncomingCallToastType,
} from './types';
import { valtioDevTools } from '@lib/utils/devTools';

export const toastsState = proxy<ToastState>({
  toasts: [],
  incomingCallToasts: [],
});

valtioDevTools(toastsState, 'toastsState');

// `createdAt` is the toast's key (hideToast, ToastsGroup), so two toasts shown
// in the same millisecond must not share it.
let lastCreatedAt = 0;

class ToastModule extends AppModule {
  override preload(): void {
    // Always on, unlike the ref-counted `add` listener in useAnvil2ToastBridge:
    // a bridged toast's action `onClick` belongs to an MFE and usually ends in
    // anvil2's `toast.dismiss(id)`, and that click can come after every
    // bridged view has unmounted. Renders nothing, so MFEs the bridge does
    // not cover stay unbridged.
    window.addEventListener('hammer-toast', (e) => {
      const detail = (e as CustomEvent<{ id?: string; eventType?: string }>)
        .detail;
      if (detail?.eventType !== 'dismiss') return;
      // anvil2's `toast.dismiss()` with no id closes every toast in every
      // anvil2 toaster on the page, whichever MFE raised it. Here that means
      // every bridged toast that is not sticky, never CCPro's own. A sticky
      // bridged toast can outlive its view, and its action may be the CSR's
      // only way back to the task, so another MFE's dismiss-all leaves it.
      if (detail.id) {
        this.hideToastById(detail.id);
      } else {
        this.hideBridgedToasts();
      }
    });
  }

  /**
   * Provides an api for the application to render Anvil Toasts.
   * Toasts will be displayed in the order they are invoked.
   * With the newest toasts at the top.
   * The default duration is 8 seconds, inline with the Anvil Toast's default duration.
   * @param toast - The toast options.
   */
  public showToast(toast: ToastOptions) {
    // if the toast already exists, do not add it again
    if (toast.id && toastsState.toasts.find((t) => t.id === toast.id)) {
      return;
    }

    lastCreatedAt = Math.max(Date.now(), lastCreatedAt + 1);
    const newToast: Toast = {
      ...toast,
      duration: toast.duration || 8000, // default duration is 8 seconds to match anvil's default duration
      createdAt: lastCreatedAt,
    };

    const newToasts = [newToast, ...toastsState.toasts];
    toastsState.toasts = newToasts;
  }

  public impersonatedUserToast() {
    this.showToast({
      status: 'critical',
      title: 'Action not allowed',
      toastBody:
        'This action cannot be performed while impersonating another user.',
    });
  }

  /**
   * Removes a toast the CSR closed or that timed out.
   * A bridged toast also has a copy in every anvil2 toaster on the page, such
   * as the one an always-mounted anvil2 Dialog renders, so this tells anvil2
   * to dismiss it there too.
   * @param createdAt - The toast's `createdAt`.
   */
  public hideToast(createdAt: number) {
    const toast = toastsState.toasts.find((t) => t.createdAt === createdAt);
    // remove toast from state
    const newToasts = [...toastsState.toasts].filter(
      (t) => t.createdAt !== createdAt,
    );
    toastsState.toasts = newToasts;

    // Never id-less: anvil2 reads a dismiss without an id as "dismiss all".
    if (toast?.bridged && toast.id) {
      anvil2Toast.dismiss(toast.id);
    }
  }

  /**
   * Removes the toast that was shown with this id. A no-op when no toast has it.
   * @param id - The id passed to `showToast`.
   */
  public hideToastById(id: string) {
    toastsState.toasts = toastsState.toasts.filter((t) => t.id !== id);
  }

  /**
   * Removes the toasts forwarded by useAnvil2ToastBridge, leaving CCPro's own
   * and the bridged toasts the bridge marked `untilClosed` (with actions or
   * `duration: false`).
   */
  public hideBridgedToasts() {
    toastsState.toasts = toastsState.toasts.filter(
      (t) => !t.bridged || t.untilClosed,
    );
  }

  /**
   * Displays an incoming call toast notification if it is not already present in the toasts state.
   *
   * @param {IncomingCallToastType} callToast - The incoming call toast object containing the details of the call.
   * @returns {void}
   */
  public showIncomingCallToast(callToast: IncomingCallToastType) {
    if (!toastsState.incomingCallToasts.find((t) => t.id === callToast.id)) {
      const newToasts = [callToast, ...toastsState.incomingCallToasts];
      toastsState.incomingCallToasts = newToasts;
    }
  }

  /**
   * Hides an incoming call toast notification based on the call ID or conversation SID.
   *
   * The reason for this is that the toast can be shown by the softphone callStarted event (callId)
   * or by the sessionOffered event (conversationSid).
   *
   * @param {string} callId - The ID of the call to hide the toast for.
   * @param {string} conversationSid - The SID of the conversation to hide the toast for.
   * @returns {void}
   */
  public hideIncomingCallToast(
    callId: string = '',
    conversationSid: string = '',
  ) {
    const newCallToasts = [...toastsState.incomingCallToasts].filter(
      (t) => t.id !== callId && t.id !== conversationSid,
    );
    toastsState.incomingCallToasts = newCallToasts;
  }
}

export default new ToastModule(appManager.events, appManager.logger('Toasts'));
